'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface OtpVerifyScreenProps {
  email: string;
  type: 'signup' | 'email';
  onVerify: (token: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
}

export default function OtpVerifyScreen({ email, type, onVerify, onResend, onBack }: OtpVerifyScreenProps) {
  // Supabase sends 6-digit codes for email confirmation (signup flow)
  // and 8-digit codes for magic-link OTP flow
  const codeLength = type === 'signup' ? 6 : 8;
  const [otp, setOtp] = useState(Array(codeLength).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  function handleChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, codeLength);
    if (!pasted) return;
    const newOtp = Array(codeLength).fill('');
    for (let i = 0; i < codeLength; i++) {
      newOtp[i] = pasted[i] || '';
    }
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((v) => !v);
    inputRefs.current[nextEmpty === -1 ? codeLength - 1 : nextEmpty]?.focus();
  }

  async function handleVerify() {
    const token = otp.join('');
    if (token.length < codeLength) {
      toast.error(`Please enter the full ${codeLength}-digit code.`);
      return;
    }
    setIsVerifying(true);
    try {
      await onVerify(token);
    } catch (error: any) {
      toast.error(error?.message || 'Invalid or expired code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResend() {
    setIsResending(true);
    try {
      await onResend();
      setCountdown(60);
      setOtp(['', '', '', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      toast.success('A new code has been sent to your email.');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="flex flex-col items-center text-center py-2">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
        <ShieldCheck size={32} className="text-primary" />
      </div>
      <h1 className="text-2xl font-bold text-foreground mb-2">Verify your identity</h1>
      <p className="text-sm text-muted-foreground mb-1">
        We sent a {codeLength}-digit code to
      </p>
      <p className="text-sm font-semibold text-foreground mb-6">{email}</p>

      {/* OTP inputs */}
      <div className="flex gap-1.5 mb-6" onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="w-9 h-13 text-center text-lg font-bold bg-card border-2 rounded-xl text-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 border-input"
            style={{ minHeight: '52px', minWidth: '36px' }}
            aria-label={`OTP digit ${i + 1}`}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={handleVerify}
        disabled={isVerifying || otp.join('').length < codeLength}
        className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed mb-4"
        style={{ minHeight: '48px' }}
      >
        {isVerifying ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Verifying...
          </>
        ) : (
          'Verify Code'
        )}
      </button>

      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        {countdown > 0 ? (
          <span>Resend code in <span className="font-semibold text-foreground">{countdown}s</span></span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="flex items-center gap-1.5 text-primary font-semibold hover:underline disabled:opacity-60"
          >
            {isResending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Resend code
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        ← Back
      </button>
    </div>
  );
}
